import { getStylePositionKey, parseStylePositionKey } from "../../positionsLib";
import type { SpecsModelPositionSnapshot } from "../types";
import type { VersionModelHost } from "./types";

type PositionGroupRule = {
  partName: string;
  elementName?: string;
};

type PositionGroup = {
  name: string;
  mutuallyExclusiveGroupNames: string[];
  unavailableWith: PositionGroupRule[];
};

export type VersionPositionStyleAssignment = {
  label: string;
  styleKey: string;
  positionIds: string[];
};

function readPositionDecorationTypeNames(position: SpecsModelPositionSnapshot) {
  const zIndexDecorationTypes = (position.generator?.sides ?? []).flatMap(
    (side) => side.zIndexByType ?? []
  );

  return [
    ...(position.allowedDecorationTypes ?? []),
    ...(position.deprecatedDecorationTypes ?? []),
    ...zIndexDecorationTypes.map((item) => item.type ?? ""),
  ];
}

export class VersionPositions {
  constructor(private readonly version: VersionModelHost) {}

  getPositions() {
    return this.version.positions;
  }

  getPositionGroups() {
    return this.version.positionGroups as PositionGroup[];
  }

  getDecorationTypeNames() {
    return this.getPositions().flatMap(readPositionDecorationTypeNames);
  }

  getPositionStyleAssignments(): VersionPositionStyleAssignment[] {
    return this.version.parts.flatMap((part) =>
      part.elements.flatMap((element) =>
        element.styles.map((style) => ({
          label: `${part.name}/${element.name}/${style.name}`,
          styleKey: getStylePositionKey(part.name, element.name, style.name),
          positionIds: [...style.positionIds],
        }))
      )
    );
  }

  addPosition(position: SpecsModelPositionSnapshot) {
    this.version.positions.push(position);
    return true;
  }

  updatePosition(previousId: string, position: SpecsModelPositionSnapshot) {
    const index = this.getPositions().findIndex(
      (candidate) => candidate.id === previousId
    );
    if (index === -1) {
      return false;
    }

    this.version.positions[index] = position;
    return true;
  }

  removePosition(positionId: string) {
    const index = this.getPositions().findIndex(
      (position) => position.id === positionId
    );
    if (index === -1) {
      return false;
    }

    this.version.positions.splice(index, 1);
    return true;
  }

  replacePositionIdInStyleAssignments(previousId: string, nextId: string) {
    if (!previousId || !nextId || previousId === nextId) {
      return true;
    }

    this.getPositionStyleAssignments().forEach((assignment) => {
      if (!assignment.positionIds.includes(previousId)) {
        return;
      }

      this.setStylePositionIds(
        assignment.styleKey,
        assignment.positionIds.map((id) => (id === previousId ? nextId : id))
      );
    });

    return true;
  }

  removePositionIdFromStyleAssignments(positionId: string) {
    this.getPositionStyleAssignments().forEach((assignment) => {
      if (!assignment.positionIds.includes(positionId)) {
        return;
      }

      this.setStylePositionIds(
        assignment.styleKey,
        assignment.positionIds.filter((id) => id !== positionId)
      );
    });

    return true;
  }

  setPositionData(args: {
    positions: SpecsModelPositionSnapshot[];
    positionGroups?: PositionGroup[] | null;
    stylePositionIds?: Record<string, string[]> | null;
  }) {
    this.version.positions.splice(
      0,
      this.version.positions.length,
      ...structuredClone(args.positions)
    );
    this.version.positionGroups.splice(
      0,
      this.version.positionGroups.length,
      ...structuredClone(args.positionGroups ?? [])
    );
    this.getPositionStyleAssignments().forEach((assignment) => {
      this.setStylePositionIds(
        assignment.styleKey,
        structuredClone(args.stylePositionIds?.[assignment.styleKey] ?? [])
      );
    });
    return true;
  }

  addPositionGroup(group: PositionGroup) {
    this.version.positionGroups.push(group);
    return true;
  }

  updatePositionGroup(previousName: string, group: PositionGroup) {
    const index = this.getPositionGroups().findIndex(
      (candidate) => candidate.name === previousName
    );
    if (index === -1) {
      return false;
    }

    this.version.positionGroups.splice(index, 1, group);
    if (previousName !== group.name) {
      this.getPositions().forEach((position) => {
        if (position.designState?.groupName === previousName) {
          position.designState.groupName = group.name;
        }
      });
      this.getPositionGroups().forEach((currentGroup) => {
        currentGroup.mutuallyExclusiveGroupNames =
          currentGroup.mutuallyExclusiveGroupNames.map((name: string) =>
            name === previousName ? group.name : name
          );
      });
    }
    return true;
  }

  removePositionGroup(groupName: string) {
    const index = this.getPositionGroups().findIndex(
      (group) => group.name === groupName
    );
    if (index === -1) {
      return false;
    }

    this.version.positionGroups.splice(index, 1);
    this.getPositionGroups().forEach((group) => {
      group.mutuallyExclusiveGroupNames =
        group.mutuallyExclusiveGroupNames.filter(
          (name: string) => name !== groupName
        );
    });
    this.getPositions().forEach((position) => {
      if (position.designState?.groupName === groupName) {
        position.designState.groupName = "";
      }
    });
    return true;
  }

  setStylePositionIds(styleKey: string, positionIds: string[]) {
    const { partName, elementName, styleName } =
      parseStylePositionKey(styleKey);
    const part = this.version.parts.find(
      (candidate) => candidate.name === partName
    );
    const element = part?.elements.find(
      (candidate) => candidate.name === elementName
    );
    const style = element?.styles.find(
      (candidate) => candidate.name === styleName
    );

    if (!style) {
      return;
    }

    style.positionIds.splice(0, style.positionIds.length, ...positionIds);
  }

  getSelectedPosition(selectedPositionId: string | null) {
    return (
      this.getPositions().find(
        (position) => position.id === selectedPositionId
      ) ?? null
    );
  }

  mutateSelectedPosition(
    selectedPositionId: string | null,
    mutator: (position: any) => void
  ) {
    const positionIndex = this.getPositions().findIndex(
      (position) => position.id === selectedPositionId
    );
    const selectedPosition =
      positionIndex === -1 ? null : this.getPositions()[positionIndex];
    if (!selectedPosition) {
      return false;
    }
    mutator(selectedPosition);
    this.version.positions.splice(positionIndex, 1, selectedPosition);
    return true;
  }

  getSelectedPositionSideSnapshots(selectedPositionId: string | null) {
    const sides =
      this.getSelectedPosition(selectedPositionId)?.generator?.sides ?? [];

    return sides.map((side: any) => ({
      ...side,
      zIndexByType: side.zIndexByType
        ? side.zIndexByType.map((item: any) => ({ ...item }))
        : side.zIndexByType,
    }));
  }

  renamePartInPositionGroupRules(previousName: string, nextName: string) {
    this.getPositionGroups().forEach((group) => {
      group.unavailableWith.forEach((rule) => {
        if (rule.partName === previousName) {
          rule.partName = nextName;
        }
      });
    });
  }

  removePartFromPositionGroupRules(partName: string) {
    this.getPositionGroups().forEach((group) => {
      group.unavailableWith = group.unavailableWith.filter(
        (rule) => rule.partName !== partName
      );
    });
  }

  moveElementInPositionGroupRules(
    previousPartName: string,
    elementName: string,
    nextPartName: string
  ) {
    this.getPositionGroups().forEach((group) => {
      group.unavailableWith.forEach((rule) => {
        if (
          rule.partName === previousPartName &&
          rule.elementName === elementName
        ) {
          rule.partName = nextPartName;
        }
      });
    });
  }
}
